import {
  calcServiceMetrics,
  fetchCostsByServices,
  fetchDiskAttachments,
  fetchPersistentDisks,
  fetchServiceSizingPoliciesByEnvironmentId,
  fetchServices,
  fetchServicesByAppId,
  selectAppById,
  selectAutoscalingEnabledById,
  selectDiskStorageDisplayByServiceId,
  selectDiskStorageLabelByServiceId,
  selectEnvironmentById,
  selectServiceRowsByAppId,
  selectServicesForTable,
  selectStackById,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useCompositeLoader, useQuery, useSelector } from "@app/react";
import {
  appServiceDisksPathUrl,
  appServicePathMetricsUrl,
  appServiceScalePathUrl,
} from "@app/routes";
import type { DeployServiceRow, DeployStack } from "@app/types";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ButtonLink } from "../button";
import { costColDef } from "../cost-estimate-tooltip";
import { DataTable, idColDef } from "../datatable";
import {
  CodeCell,
  CurrencyCell,
  DateTimeCell,
  EnvironmentCell,
  ServiceCell,
} from "../datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { Group } from "../group";
import { IconInfo } from "../icons";
import { TitleBar } from "../resource-list-view";
import { ScaleRecsView } from "../scale-recs";
import { tokens } from "../tokens";
import { Tooltip } from "../tooltip";

// ─── Disk-storage indicator ───────────────────────────────────────────────────
// Read-only "Disk Storage" cell: shows where a persistent disk is attached to a
// service. Pure label logic lives in diskStorageLabel + diskStorageDisplay
// (disk-attachment module); this cell just reads the per-service attachments +
// disk handles from the store and renders the result. Loaded by
// DiskStorageLoader and only shown when the feature flag is on, so non-enrolled
// orgs make no extra requests / see no col. The cell is wrapped in a Link to
// the service's Disk Storage tab.
const DiskStorageCell = ({ data }: { data: DeployServiceRow }) => {
  const display = useSelector((s) =>
    selectDiskStorageDisplayByServiceId(s, { serviceId: data.id }),
  );
  const label = useSelector((s) =>
    selectDiskStorageLabelByServiceId(s, { serviceId: data.id }),
  );

  if (label === null) {
    return <span className={tokens.type["normal lighter"]}>N/A</span>;
  }

  // Single-attachment rows render the handle on top and the size in a muted
  // color underneath. Multi-attachment rows fall back to the single-line "{n}
  // Attached" summary returned by selectDiskStorageLabelByServiceId.
  const body =
    display !== null ? (
      <>
        <div className={tokens.type.darker}>{display.handle}</div>
        <div className={tokens.type["normal lighter"]}>{display.size} GB</div>
      </>
    ) : (
      <span className={tokens.type.darker}>{label}</span>
    );

  return (
    <Link
      to={appServiceDisksPathUrl(data.appId, data.id)}
      className="hover:underline"
    >
      {body}
    </Link>
  );
};

// Loads the org-wide persistent-disk + attachment data the indicator reads from.
// Mirrors the fetch idiom in service-disk-attachments-list.tsx (no new
// deploy-api surface). Rendered only when the feature is enabled. Renders nothing.
const DiskStorageLoader = () => {
  useQuery(fetchPersistentDisks());
  useQuery(fetchDiskAttachments());
  return null;
};

// ─── Shared column definitions ───────────────────────────────────────────────

const colDefs: Record<string, DataTableColDef<DeployServiceRow>> = {
  service: {
    field: "processType",
    headerName: "Service",
    width: 300,
    resizable: true,
    suppressAutoSize: false,
    suppressSizeToFit: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <ServiceCell id={data.id} />
    ),
    // AG Grid types data as T | undefined in ValueGetterParams to account for
    // non-data row types (group rows, pinned rows) where data does not exist
    valueGetter: ({ data }: { data?: DeployServiceRow }) =>
      data ? `${data.resourceHandle} ${data.processType}` : "",
  },
  command: {
    field: "command",
    headerName: "Command",
    initialHide: true,
    resizable: true,
    suppressAutoSize: false,
    suppressSizeToFit: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <CodeCell value={data.command} />
    ),
  },
  id: {
    field: "id",
    sortable: true,
    sort: "asc",
    ...idColDef,
    resizable: true,
  },
  environment: {
    field: "envHandle",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon />
    ),
  },
  details: {
    field: "containerCount",
    headerName: "Details",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => {
      const metrics = calcServiceMetrics(data);
      return (
        <div className={tokens.type.darker}>
          <div>
            {data.containerCount} Container
            {data.containerCount === 1 ? "" : "s"}
          </div>
          <div className={tokens.type["normal lighter"]}>
            {metrics.containerSizeGB} GB · {metrics.cpuShare} CPU
          </div>
        </div>
      );
    },
  },
  diskStorage: {
    colId: "diskStorage",
    field: "diskStorageSort",
    headerName: "Disk Storage",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <DiskStorageCell data={data} />
    ),
  },
  efficiency: {
    field: "savings",
    headerName: "Container Efficiency",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <ScaleRecsView service={data} />
    ),
  },
  scaleRecommendation: {
    field: "scaleRec",
    headerName: "Scale Recommendation",
    valueGetter: ({ data }) =>
      data?.scaleRec === "none" ? null : data?.scaleRec,
    hide: true,
  },
  cost: {
    field: "cost",
    ...costColDef,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
  scaleAction: {
    colId: "scaleAction",
    headerName: "Actions",
    sortable: false,
    width: 90,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <ButtonLink
        size="sm"
        to={appServiceScalePathUrl(data.appId, data.id)}
        variant="primary"
      >
        Scale
      </ButtonLink>
    ),
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialSort: "desc",
    sortable: true,
    width: 180,
    resizable: true,
    suppressAutoSize: false,
    suppressSizeToFit: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployServiceRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
};

// ─── AppServicesByOrg ─────────────────────────────────────────────────────────

// Disk Storage sits after Details and before Cost/mo (cost stays last
// before Actions), matching Amy's mock.
const appServicesByOrgColDefs: DataTableColDef<DeployServiceRow>[] = [
  colDefs.service,
  colDefs.id,
  colDefs.command,
  colDefs.environment,
  colDefs.details,
  colDefs.diskStorage,
  colDefs.createdAt,
  colDefs.efficiency,
  colDefs.scaleRecommendation,
  colDefs.cost,
  colDefs.scaleAction,
];

export function AppServicesByOrg() {
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchCostsByServices({ orgId }));
  const { isLoading, isInitialLoading } = useCompositeLoader([fetchServices]);
  const services = useSelector((s) => selectServicesForTable(s));

  return (
    <Group size="xl">
      <DiskStorageLoader />
      <TitleBar description="Services determine the number of containers of your app and the memory and CPU limits for your app.">
        Services
      </TitleBar>
      <DataTable<DeployServiceRow>
        rowData={services}
        colDefs={appServicesByOrgColDefs}
        isLoading={!services.length && (isLoading || isInitialLoading)}
        syncQueryParams={true}
      />
    </Group>
  );
}

// ─── AppServicesByApp ─────────────────────────────────────────────────────────

const AppServiceCostCell = ({
  data,
  stack,
}: {
  data: DeployServiceRow;
  stack: DeployStack;
}) => {
  const autoscalingEnabled = useSelector((s) =>
    selectAutoscalingEnabledById(s, { id: data.serviceSizingPolicyId }),
  );
  const stackHasAutoscaling =
    stack.verticalAutoscaling || stack.horizontalAutoscaling;
  const hideCost = stackHasAutoscaling && autoscalingEnabled;

  if (hideCost) {
    return (
      <Group variant="horizontal" size="sm" className="items-center">
        <div>Unavailable</div>
        <Tooltip
          fluid
          className="inline-block"
          text="Cost cannot be estimated when autoscaling is enabled."
        >
          <IconInfo variant="sm" className="opacity-50 hover:opacity-100" />
        </Tooltip>
      </Group>
    );
  }
  return <CurrencyCell value={data.cost} />;
};

export function AppServicesByApp({ appId }: { appId: string }) {
  const app = useSelector((s) => selectAppById(s, { id: appId }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const stack = useSelector((s) =>
    selectStackById(s, { id: environment.stackId }),
  );
  useQuery(
    fetchServicesByAppId({
      id: app.id,
      withDeleted: true,
      waitForBootup: true,
    }),
  );
  useQuery(
    fetchServiceSizingPoliciesByEnvironmentId({ id: app.environmentId }),
  );
  const services = useSelector((s) =>
    selectServiceRowsByAppId(s, { appId }),
  ).filter((s) => !s.deletedAt);

  const appServicesByAppColDefs = useMemo<DataTableColDef<DeployServiceRow>[]>(
    () => [
      colDefs.service,
      colDefs.id,
      colDefs.command,
      colDefs.details,
      colDefs.diskStorage,
      {
        field: "cost",
        ...costColDef,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<DeployServiceRow>) => (
          <AppServiceCostCell data={data} stack={stack} />
        ),
      },
      colDefs.createdAt,
      colDefs.efficiency,
      colDefs.scaleRecommendation,
      {
        colId: "scaleAction",
        headerName: "Actions",
        sortable: false,
        width: 160,
        resizable: false,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        cellRenderer: ({
          data,
        }: DataTableCellRendererProps<DeployServiceRow>) => (
          <Group size="sm" variant="horizontal">
            <ButtonLink
              size="sm"
              to={appServicePathMetricsUrl(app.id, data.id)}
              variant="primary"
            >
              Metrics
            </ButtonLink>
            <ButtonLink
              size="sm"
              to={appServiceScalePathUrl(app.id, data.id)}
              variant="primary"
            >
              Scale
            </ButtonLink>
          </Group>
        ),
      },
    ],
    [app.id, stack],
  );

  return (
    <>
      <DiskStorageLoader />
      <DataTable<DeployServiceRow>
        rowData={services}
        colDefs={appServicesByAppColDefs}
      />
    </>
  );
}
